/**
 * PUT `pharmacy/questionnaire-review/{orderId}` — persists the latest
 * questionnaire answers and reviewer metadata for a single PA order.
 *
 * Wraps {@link API_ENDPOINTS.QUESTIONNAIRE_REVIEW} with bearer-auth headers,
 * supports cancellation via an optional {@link AbortSignal}, and surfaces
 * errors through the shared `logError` channel.
 *
 * @module api/putCalls/putLatestQuestionnaireWithResponse
 */
import {
  UpdateQuestionnaireReviewRequest,
  UpdateQuestionnaireReviewResponse,
} from "../../data-model/questionnaireReview";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logDataToConsole, logError } from "../../utils/customLogger";
import { getAuthHeaders, handleApiResponse } from "../utils/apiHelpers";

/**
 * Persists the updated questionnaire for the supplied PA order.
 *
 * @param orderId - PA order id appended to the endpoint path
 * @param body    - {@link UpdateQuestionnaireReviewRequest} payload
 * @param signal  - Optional `AbortSignal` used to cancel the in-flight request
 * @returns       Parsed {@link UpdateQuestionnaireReviewResponse} envelope
 * @throws        Rethrows any non-2xx HTTP error or abort error after logging
 */
export const putLatestQuestionnaireWithResponse = async (
  orderId: string,
  body: UpdateQuestionnaireReviewRequest,
  signal?: AbortSignal,
): Promise<UpdateQuestionnaireReviewResponse> => {
  if (!orderId) {
    throw new Error("orderId is required to update questionnaire review");
  }

  const url = `${API_ENDPOINTS.QUESTIONNAIRE_REVIEW}${encodeURIComponent(orderId)}`;

  try {
    const authHeaders = await getAuthHeaders();

    const requestOptions: RequestInit = {
      method: "PUT",
      headers: { ...authHeaders, "Content-Type": "application/json" },
      redirect: "follow",
      body: JSON.stringify(body),
      signal,
    };

    const response = await fetch(url, requestOptions);
    const result = (await handleApiResponse(
      response,
    )) as UpdateQuestionnaireReviewResponse;
    logDataToConsole("putLatestQuestionnaireWithResponse", result);
    return result;
  } catch (error) {
    if ((error as Error).name === "AbortError") {
      logDataToConsole("putLatestQuestionnaireWithResponse", "Request aborted");
      throw error;
    }
    logError(error as Error, "Error in putLatestQuestionnaireWithResponse");
    throw error;
  }
};
